import {
  Button,
  FormContextProvider,
  Heading,
  Input,
  InputField,
  RadioGroupField,
} from '@nbirl/nbi-common-ui-libs';
import { Form, FormikConfig } from 'formik';

import { ActionsBar, BackButton } from '../../../../shared/components';
import { PATHS } from '../../../../shared/constants';
import { useCurrentUser } from '../../../../shared/context';
import { Role } from '../../../../shared/enum';
import { Folder } from '../../../resources/folders/models';
import { User } from '../../models';
import { EnableUserButton } from '../enable-user-button.component';
import { FolderManagement } from '../folder-management.component';
import { StatusPill } from '../status-pill.component';
import { getRoleRadioGroupItems, userFormSchema } from './user-form.form';
import { UserFormValues } from './user-form.model';

export type UserFormProps = {
  folders: Folder[];
  onSubmit: FormikConfig<UserFormValues>['onSubmit'];
  user?: User;
  disabledFolders?: Folder[];
  onStatusChange?: () => void;
};

export const UserForm = ({
  folders,
  onSubmit,
  user,
  disabledFolders,
  onStatusChange,
}: UserFormProps) => {
  const { permissions } = useCurrentUser();

  return (
    <FormContextProvider<UserFormValues>
      initialValues={{
        email: user?.email || '',
        name: user?.name || '',
        mobileNumber: user?.mobileNumber || '+353',
        role: user?.role || Role.User,
        folderIds: user?.folders?.map(({ id }) => id) ?? [],
      }}
      validationSchema={userFormSchema}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting }) => (
        <Form data-testid="user-form">
          <div className="pb-24">
            <div className="flex items-center space-x-2 mb-4">
              <BackButton
                path={PATHS.UserManagement}
                label="Go back to users page"
              />
              <Heading>{user ? 'Edit User' : 'Add User'}</Heading>
            </div>
            {user && (
              <div className="flex items-center space-x-2 mb-8">
                <span className="text-gray-500">Status:</span>
                <StatusPill enabled={user.enabled} />
              </div>
            )}
            <div className="flex space-x-32">
              <div className="flex-1 space-y-6">
                <Heading size="md">User Details</Heading>
                <InputField name="name" showRequiredIndicator={false}>
                  {(field) => (
                    <Input type="text" autoComplete="name" {...field} />
                  )}
                </InputField>
                <InputField name="email" showRequiredIndicator={false}>
                  {(field) => (
                    <Input type="email" autoComplete="username" {...field} />
                  )}
                </InputField>
                <InputField
                  name="mobileNumber"
                  helpText="Used for Two Factor Authentication"
                  showRequiredIndicator={false}
                >
                  {(field) => (
                    <Input type="tel" autoComplete="tel" {...field} />
                  )}
                </InputField>
              </div>
              <div className="flex-1">
                <Heading size="md" className="mb-8">
                  Roles
                </Heading>
                <RadioGroupField
                  label=""
                  name="role"
                  items={getRoleRadioGroupItems(permissions)}
                  showRequiredIndicator={false}
                />
              </div>
              <div className="flex-1">
                <FolderManagement
                  chosenRole={values.role}
                  folders={folders}
                  disabledFolders={disabledFolders}
                />
              </div>
            </div>
          </div>
          <ActionsBar>
            {user && (
              <EnableUserButton
                userId={user.id}
                userEnabled={user.enabled}
                disabled={isSubmitting}
                onStatusChange={onStatusChange}
              />
            )}
            <Button
              type="submit"
              isPending={isSubmitting}
              disabled={user && !user.enabled}
            >
              {user ? 'Save' : 'Add User'}
            </Button>
          </ActionsBar>
        </Form>
      )}
    </FormContextProvider>
  );
};
